<script setup lang="ts">
  import 'swiper/css/navigation'
  import type Swiper from 'swiper'

  import { onClickOutside } from '@vueuse/core'
  import { Swiper as SwiperContainer, SwiperSlide } from 'swiper/vue'
  import 'swiper/css'

  import { useScreenSize } from '@/shared/lib'
  import { ProxyImage } from '@/shared/ui/components'
  import { CrossIcon } from '@/shared/ui/svg'

  const props = withDefaults(
    defineProps<{
      controller?: Nullable<Swiper>
      images: string[]
      modules?: unknown[]
    }>(),
    {
      modules: () => [],
      controller: null,
    },
  )

  const emit = defineEmits<{
    (event: 'close-lightbox'): void
    (event: 'set-swiper', swiper: Swiper): void
  }>()

  const showNavigation = computed(() => {
    return props.images.length > 1
  })

  const { isMobile } = storeToRefs(useScreenSize())

  const lightboxContainerRef = ref<HTMLDivElement>()

  onClickOutside(lightboxContainerRef, () => {
    emit('close-lightbox')
  })

  const keydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      emit('close-lightbox')
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', keydownHandler)
  })
  onBeforeUnmount(() => {
    document.removeEventListener('keydown', keydownHandler)
  })
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-999 flex h-full w-full flex-col items-center justify-center bg-black/50">
      <CrossIcon
        v-if="isMobile"
        class="absolute top-5 right-5 z-999 size-5 cursor-pointer text-(--color-icon-primary)"
        @click="emit('close-lightbox')"
      />
      <div
        class="flex h-full max-h-300 w-full max-w-250 flex-col items-center justify-center gap-5 max-sm:items-start max-sm:justify-start"
      >
        <SwiperContainer
          ref="lightboxContainerRef"
          :space-between="0"
          :modules="modules"
          :controller="{ control: controller }"
          class="h-4/5 w-full rounded-[20px] max-sm:h-full max-sm:rounded-none [&_.swiper-button-next]:text-(--color-icon-primary) [&_.swiper-button-next]:[--swiper-navigation-size:30px] [&_.swiper-button-prev]:text-(--color-icon-primary) [&_.swiper-button-prev]:[--swiper-navigation-size:30px]"
          :navigation="showNavigation"
          :keyboard="{
            enabled: true,
          }"
          @swiper="emit('set-swiper', $event)"
        >
          <SwiperSlide
            v-for="image in images"
            :key="image"
            class="flex items-center justify-center"
          >
            <ProxyImage
              :get-image-options="{ id: image }"
              class="size-full object-scale-down"
            />
          </SwiperSlide>
        </SwiperContainer>
      </div>
    </div>
  </Teleport>
</template>
